/** Recipe Bank (Demo Mode) - a card-library view of the full RecipeV2 catalogue, distinct from
 * the RecipeV2 table list (`/store/recipes-v2`). Net-new Demo screen: mocked data only, imported
 * directly from `src/mocks/data/recipeV2Data.ts` per the Demo Screen Structure pattern - no
 * typed client, no MSW handler. Gated behind the existing `RecipeV2Read` permission. */

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close, ExpandMore, PictureAsPdf, Search } from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { mockRecipesV2, mockAllergens, mockRecipeV2Details } from '../../mocks/data/recipeV2Data';
import RecipeCard from './components/RecipeCard';
import { AllergenStatus, DietaryAttributeStatus } from './types';
import { buildRecipeBookPdf } from './utils/generateRecipeBookPdf';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);

const RecipeBankPage = () => {
  const { t } = useTranslation('recipeBank');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isGeneratingRecipeBook, setIsGeneratingRecipeBook] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [freeFromAllergenCode, setFreeFromAllergenCode] = useState('');
  const [dietaryCode, setDietaryCode] = useState('');

  const categories = useMemo(
    () => Array.from(new Set(mockRecipesV2.map((r) => r.primaryCategory))).sort(),
    [],
  );

  const dietaryOptions = useMemo(() => {
    const seen = new Map<string, string>();
    mockRecipesV2.forEach((r) => {
      r.dietaryAttributes
        .filter((a) => a.status === DietaryAttributeStatus.Suitable || a.status === DietaryAttributeStatus.Certified)
        .forEach((a) => seen.set(a.code, a.name));
    });
    return Array.from(seen.entries()).map(([code, name]) => ({ code, name }));
  }, []);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
        setDebouncedSearch(value);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleClearFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCategoryFilter('');
    setFreeFromAllergenCode('');
    setDietaryCode('');
    setSearch('');
    setDebouncedSearch('');
  };

  const handleGenerateRecipeBook = () => {
    setIsGeneratingRecipeBook(true);
    try {
      const doc = buildRecipeBookPdf(mockRecipesV2, mockRecipeV2Details);
      doc.save(`recipe-book-${new Date().toISOString().split('T')[0]}.pdf`);
    } finally {
      setIsGeneratingRecipeBook(false);
    }
  };

  const filteredRecipes = mockRecipesV2.filter((recipe) => {
    if (categoryFilter && recipe.primaryCategory !== categoryFilter) return false;
    if (freeFromAllergenCode) {
      const entry = recipe.allergenSummary.find((a) => a.allergenCode === freeFromAllergenCode);
      if (entry && entry.status !== AllergenStatus.FreeFrom) return false;
    }
    if (dietaryCode) {
      const entry = recipe.dietaryAttributes.find((a) => a.code === dietaryCode);
      if (!entry || (entry.status !== DietaryAttributeStatus.Suitable && entry.status !== DietaryAttributeStatus.Certified)) return false;
    }
    if (debouncedSearch) {
      const haystack = `${recipe.customerFacingName ?? ''} ${recipe.description}`.toLowerCase();
      if (!haystack.includes(debouncedSearch.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <PageArea>
      <Stack width="100%"
        spacing={2}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center">
          <PageTitle subTitle={t('subtitle', 'Browse the full recipe catalogue')}>
            {t('title', 'Recipe Bank')}
          </PageTitle>
          <Button
            variant="secondary"
            startIcon={isGeneratingRecipeBook ? <CircularProgress size={16} /> : <PictureAsPdf />}
            disabled={isGeneratingRecipeBook}
            onClick={handleGenerateRecipeBook}
          >
            {isGeneratingRecipeBook
              ? t('generatingRecipeBook', 'Generating...')
              : t('generateRecipeBook', 'Generate Recipe Book')}
          </Button>
        </Stack>

        <Grid container
          spacing={2}
          alignItems="center">
          <Grid item
            sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder={t('searchPlaceholder', 'Search recipes...')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <Close
                      sx={{ cursor: 'pointer', fontSize: 18 }}
                      onClick={() => handleSearchChange('')}
                    />
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ minWidth: 240, mb: 0 }}
            />
          </Grid>

          <Grid item>
            <QuickFilterDivider />
          </Grid>

          <Grid item>
            <Grid container
              spacing={1}
              alignItems="center">
              {categories.map((category) => (
                <Grid item
                  key={category}>
                  <Chip
                    label={category}
                    color={categoryFilter === category ? 'primary' : 'default'}
                    onClick={() => setCategoryFilter((prev) => (prev === category ? '' : category))}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Accordion
          variant="borderless"
          expanded={isAdvancedOpen}
          onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%">
              <Typography variant="textSM"
                fontWeight={600}>
                {t('advancedFilters', 'Advanced Filters')}
              </Typography>
              <Button variant="secondary"
                size="md"
                onClick={handleClearFilters}>
                {t('clearFilters', 'Clear Filters')}
              </Button>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container
              spacing={2}>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t('filters.category', 'Category')}
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category}
                      value={category}>{category}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t('filters.freeFrom', 'Free From')}
                  value={freeFromAllergenCode}
                  onChange={(e) => setFreeFromAllergenCode(e.target.value)}
                >
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {mockAllergens.map((allergen) => (
                    <MenuItem key={allergen.code}
                      value={allergen.code}>{allergen.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t('filters.dietary', 'Dietary Attribute')}
                  value={dietaryCode}
                  onChange={(e) => setDietaryCode(e.target.value)}
                >
                  <MenuItem value="">{t('filters.all', 'All')}</MenuItem>
                  {dietaryOptions.map((option) => (
                    <MenuItem key={option.code}
                      value={option.code}>{option.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {filteredRecipes.length === 0 ? (
          <Stack alignItems="center"
            sx={{ py: 8 }}>
            <Typography variant="textMD"
              color="text.secondary">
              {t('noResults', 'No recipes match the selected filters')}
            </Typography>
          </Stack>
        ) : (
          <Grid container
            spacing={3}>
            {filteredRecipes.map((recipe) => (
              <Grid item
                key={recipe.id}
                xs={12}
                sm={6}
                md={4}
                lg={3}>
                <RecipeCard
                  recipe={recipe}
                  onClick={() => navigate(`/store/recipe-bank/${recipe.id}`)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    </PageArea>
  );
};

export default RecipeBankPage;
